// Content Security and HTML sanitization
import DOMPurify from 'dompurify';

// Secure HTML rendering configuration
export const createSecureDOMPurify = () => {
  const purify = DOMPurify();

  // Configure DOMPurify for maximum security
  purify.addHook('beforeSanitizeElements', (node) => {
    // Remove any script tags, even if they seem safe
    if (node.nodeName === 'SCRIPT') {
      (node as Element).remove();
      return;
    }

    // Remove dangerous attributes
    const dangerousAttrs = ['onload', 'onerror', 'onclick', 'onmouseover'];
    const element = node as Element;
    dangerousAttrs.forEach(attr => {
      if (element.hasAttribute && element.hasAttribute(attr)) {
        element.removeAttribute(attr);
      }
    });
  });

  return purify;
};

// Sanitize HTML content with strict rules
export const sanitizeHTML = (html: string): string => {
  const purify = createSecureDOMPurify();

  return purify.sanitize(html, {
    ALLOWED_TAGS: [
      'p', 'br', 'strong', 'em', 'u', 'i', 'b',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li', 'blockquote', 'a'
    ],
    ALLOWED_ATTR: ['href', 'target', 'rel'],
    ALLOW_DATA_ATTR: false,
    FORBID_CONTENTS: ['script', 'style', 'iframe', 'object', 'embed'],
    FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input'],
    FORBID_ATTR: [
      'onclick', 'onload', 'onerror', 'onmouseover', 'onfocus', 'onblur',
      'style', 'class', 'id'
    ]
  });
};

// Sanitize user input text
export const sanitizeText = (text: string, maxLength?: number): string => {
  if (!text) return '';

  // Remove HTML tags
  let sanitized = text.replace(/<[^>]*>/g, '');

  // Remove potentially dangerous characters
  sanitized = sanitized.replace(/[<>'"&]/g, (char) => {
    const entities: Record<string, string> = {
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#x27;',
      '&': '&amp;'
    };
    return entities[char] || char;
  });

  // Trim and limit length
  sanitized = sanitized.trim();
  if (maxLength && sanitized.length > maxLength) {
    sanitized = sanitized.substring(0, maxLength);
  }

  return sanitized;
};

// Validate and sanitize URLs
export const sanitizeURL = (url: string): string | null => {
  try {
    const parsed = new URL(url);

    // Only allow safe protocols
    const allowedProtocols = ['http:', 'https:', 'mailto:', 'tel:'];
    if (!allowedProtocols.includes(parsed.protocol)) {
      return null;
    }

    // Block localhost and private IPs in production unless explicitly allowed in local environment
    if (import.meta.env.PROD) {
      const hostname = parsed.hostname.toLowerCase();
      const isLocalHost = hostname === 'localhost' || hostname === '127.0.0.1';
      const isPrivateIP = hostname.startsWith('192.168.') ||
        hostname.startsWith('10.') ||
        hostname.startsWith('172.');

      // If we are in local environment mode, we allow these even in production-like builds (like local preview)
      const isLocalEnv = import.meta.env.VITE_ENVIRONMENT === 'local';

      if ((isLocalHost || isPrivateIP) && !isLocalEnv) {
        return null;
      }
    }

    return parsed.toString();
  } catch {
    return null;
  }
};

// Content validation and threat detection
export const validateContent = (content: string): {
  isValid: boolean;
  threats: string[];
  sanitized: string;
} => {
  const threats: string[] = [];

  // Check for script injection attempts
  if (/<script|javascript:|vbscript:|data:text\/html/i.test(content)) {
    threats.push('Script injection detected');
  }

  // Check for event handlers
  if (/on\w+\s*=/i.test(content)) {
    threats.push('Event handler injection detected');
  }

  // Check for iframe/embed attempts
  if (/<iframe|<embed|<object/i.test(content)) {
    threats.push('Frame injection detected');
  }

  // Check for style injection
  if (/<style|expression\(|behavior:/i.test(content)) {
    threats.push('Style injection detected');
  }

  return {
    isValid: threats.length === 0,
    threats,
    sanitized: sanitizeHTML(content)
  };
};

// Legacy function exports for backward compatibility
export const validateAdminContent = (content: string, type?: string): {
  isValid: boolean;
  threats: string[];
  sanitized: string;
  message?: string;
} => {
  const result = validateContent(content);
  return {
    ...result,
    message: result.threats.length > 0 ? result.threats.join(', ') : undefined
  };
};

export const sanitizeHtmlContent = sanitizeHTML;

// Rate limiting placeholder - returns true to allow all operations
export const checkContentUpdateRateLimit = (userId?: string): boolean => {
  // In a real implementation, this would check rate limits
  // For now, always return true to prevent blocking
  return true;
};